import { Actions, DockLocation } from 'flexlayout-react';
import _ from 'lodash';
import { sqLicenseManagementStore } from '@/core/core.stores';
import { isSystemTest } from '@/core/utilities';
import { Feature } from '@/licenseManagement/licenseManagement.constants';
import { hasValidFeatureInLicense } from '@/licenseManagement/licenseManagement.utilities';
import {
  resetJournalToolbar,
  resetLayout,
  setAssetGroupEditorVisible,
  setFormulaInTab,
  setJournalTabVisible,
  setTableDefinitionEditorVisible,
  setTrendVisible,
} from '@/main/layout.actions';
import {
  BORDER_TAB_HEIGHT,
  BORDER_WIDTH,
  SIDE_PANEL_INITIAL_WIDTH,
  actionBoardLayoutJson,
  assetGroupTabId,
  assetGroupTabJson,
  calculationHierarchyTabId,
  calculationHierarchyTabJson,
  capsulePanelTabId,
  capsulePanelTabJson,
  capturesTabId,
  capturesTabJson,
  collapsibleSidePanelId,
  commentsTabId,
  dataPanelTabId,
  detailsPanelTabId,
  formulaHelpTabId,
  formulaHelpTabJson,
  journalPanelTabId,
  journalTabId,
  mobileOrganizerLayoutJSON,
  mobileWorksheetLayoutJSON,
  organizerLayoutJson,
  propertiesTabId,
  storedTableTabId,
  storedTableTabJson,
  tableDefinitionTabId,
  tableDefinitionTabJson,
  toolsPanelTabId,
  trendTabId,
  trendTabSetId,
  vantageEmbeddedLayoutJson,
  vantageLayoutJson,
  vantageTableTabId,
  worksheetLayoutJSON,
  worksheetsTabId,
} from '@/main/layoutDefinitions';
import { setToolsTabVisible } from '@/main/layoutPreferences';
import { getWorkbookDisplayMode } from '@/main/routing.utilities';
import { hasScalingTableCapability } from '@/services/authorization.service';
import {
  capturesEnabled,
  isScalingTableEditorEnabled,
  isStoredTableItemFinderEnabled,
} from '@/services/systemConfiguration.utilities';
import { isMobileDevice } from '@/utilities/utilities';
import { WORKBOOK_DISPLAY } from '@/workbook/workbook.constants';
import { WORKSHEET_VIEW } from '@/worksheet/worksheet.constants';
import type { IJsonModel, IJsonTabNode, Model, TabNode } from 'flexlayout-react';
import type { WorksheetView } from '@/worksheet/worksheet.constants';

/**
 * Determines if a particular worksheet view is currently selected for display.
 * target - A worksheet target (i.e. WORKSHEET_TARGET.DATA_PANEL or WORKSHEET_TARGET.WORKSHEET).
 * key - A worksheet key (e.g. WORKSHEET_VIEW.TREND, WORKSHEET_VIEW.EXPLORE, etc.). If this
 *                       value is not defined, then only the target parameter is used to determine if
 *                       the view is selected.
 * returns True if a view matching the supplied target and key is selected, false otherwise or
 *                    if transitioning to a worksheet.
 */
export function isSelectedView(target: string, key?: string, view?: WorksheetView): boolean {
  const currentTarget = view?.target;
  const currentKey = view?.key;
  return currentTarget === target && (!key || currentKey === key);
}

export const detailsAndCapsulePanelVisibilityByView = (view: string) => {
  return { details: true, capsules: view !== WORKSHEET_VIEW.TREEMAP && view !== WORKSHEET_VIEW.TABLE };
};

export const getLayoutStorageIdentifier = (
  viewOnlyMode: boolean,
  isTopic: boolean,
  isVantage: boolean,
  isActionBoard: boolean,
  isEmbedded = false,
) => {
  let identifier =
    (isTopic && 'organizerLayout') ||
    (isVantage && (isEmbedded ? 'vantageEmbeddedLayoutJson' : 'vantageLayoutJson')) ||
    (isActionBoard && 'actionBoardLayoutJson') ||
    'worksheetLayout';
  if (viewOnlyMode) {
    identifier += '_viewOnly';
  }
  if (isMobileDevice()) {
    identifier += '_mobile';
  }
  return identifier;
};

export const getInitialLayout = (
  isTopic: boolean,
  isVantage: boolean,
  isActionBoard: boolean,
  isEmbedded: boolean,
  availableWidth: number,
) => {
  if (isMobileDevice()) {
    return (
      (isTopic && mobileOrganizerLayoutJSON) ||
      (isVantage && (isEmbedded ? vantageEmbeddedLayoutJson : vantageLayoutJson)) ||
      (isActionBoard && actionBoardLayoutJson) ||
      mobileWorksheetLayoutJSON
    );
  }

  return applySidePanelInitialWeight(
    (isTopic && organizerLayoutJson) ||
      (isVantage && (isEmbedded ? vantageEmbeddedLayoutJson : vantageLayoutJson)) ||
      (isActionBoard && actionBoardLayoutJson) ||
      worksheetLayoutJSON,
    collapsibleSidePanelId,
    isTopic ? 'editorTabSetId' : 'trendarea',
    availableWidth - BORDER_WIDTH - BORDER_TAB_HEIGHT,
    SIDE_PANEL_INITIAL_WIDTH,
  );
};

export const addCapsulePanel = (layoutJson: Model) => {
  if (!layoutJson.getNodeById(capsulePanelTabId)) {
    const detailsPanelParent = layoutJson.getNodeById(detailsPanelTabId)?.getParent();
    detailsPanelParent &&
      layoutJson.doAction(Actions.addNode(capsulePanelTabJson, detailsPanelParent.getId(), DockLocation.RIGHT, -1));
  }
  return layoutJson;
};

export const addCalculationHierarchyPanel = (layoutJson: Model) => {
  if (!layoutJson.getNodeById(calculationHierarchyTabId)) {
    const trendPanelParent = layoutJson.getNodeById(trendTabId)?.getParent();
    trendPanelParent &&
      layoutJson.doAction(
        Actions.addNode(calculationHierarchyTabJson, trendPanelParent.getId(), DockLocation.CENTER, -1),
      );
  }
  return layoutJson;
};

/** Whether the workbook rail offers the Captures tab: Analysis workbooks with the feature enabled. */
export const shouldShowCapturesTab = (isTopic: boolean, isVantage: boolean, isActionBoard: boolean): boolean =>
  capturesEnabled() && !isTopic && !isVantage && !isActionBoard;

/**
 * Adds the Captures tab to the left border beside the Worksheets tab, or removes it, so the tab
 * tracks the feature flag and the workbook type across both fresh and persisted layouts.
 */
export const manageCapturesTab = (layoutModel: Model, shouldShow: boolean) => {
  const capturesTab = layoutModel.getNodeById(capturesTabId) as TabNode | undefined;

  if (shouldShow && !capturesTab) {
    layoutModel.doAction(Actions.addNode(capturesTabJson, 'border_left', DockLocation.CENTER, -1, false));
  } else if (!shouldShow && capturesTab) {
    layoutModel.doAction(Actions.deleteTab(capturesTabId));
  }
};

export const getVantageEvidenceTabSetId = (layoutModel: Model): string | undefined =>
  layoutModel.getNodeById(vantageTableTabId)?.getParent()?.getId();

/**
 * Removes a flag-gated tab from the layout when its feature is disabled. These tabs
 * are opened on demand (e.g. the Run Log tab from the Vantage Settings panel), so this
 * never adds — it only cleans up a tab that was opened while the flag was on and then
 * persisted into the saved layout, so a disabled feature can't linger.
 */
export const removeTabWhenDisabled = (layoutModel: Model, tabId: string, isEnabled: boolean) => {
  if (!isEnabled && layoutModel.getNodeById(tabId)) {
    layoutModel.doAction(Actions.deleteTab(tabId));
  }
};

export const manageFullHeightTab = (
  layoutModel: Model | undefined,
  addTab: boolean,
  tabId: string,
  tabJson: IJsonTabNode,
  visibilityCallback: (visible: boolean) => void,
) => {
  if (!layoutModel) return;

  if (addTab) {
    let tabNode = layoutModel.getNodeById(tabId);
    if (!tabNode) {
      const tabSetId = layoutModel.getNodeById(trendTabId)?.getParent()?.getId();
      if (tabSetId === undefined) return;
      layoutModel.doAction(Actions.addNode(tabJson, tabSetId, DockLocation.CENTER, -1));
      tabNode = layoutModel.getNodeById(tabId);
      tabNode?.setEventListener('visibility', ({ visible }) => {
        visibilityCallback(visible);
      });
    }

    // Re-opening an existing editor should bring its tab to the front, not just ensure it exists.
    layoutModel.doAction(Actions.selectTab(tabId));
  } else {
    layoutModel.doAction(Actions.deleteTab(tabId));
  }
};

export const getParentId = (layoutJson: Model, tabSetId: string) => {
  const tab = layoutJson.getNodeById(tabSetId);
  const children = tab?.getParent()?.getChildren();
  const types = children?.map((child) => child.getType());
  return _.uniq(types).length === 1 && tab?.getParent()?.getId() !== 'root' ? tab?.getParent()?.getId() : tabSetId;
};

export const removeFormulaHelpTab = (layoutJson: Model) => {
  // when deleting the formula help tab we have to make sure we set the parent panel with to avoid jumping as it
  // re-creates tab set when removing the help tab
  const tabSetParent = layoutJson.getNodeById(formulaHelpTabId)?.getParent();
  const parentId = getParentId(layoutJson, tabSetParent?.getId() || '');
  const formulaHelpPanelParent = layoutJson.getNodeById(parentId!);
  const width = formulaHelpPanelParent?.getRect()?.width ?? 100;
  layoutJson.doAction(Actions.deleteTab(formulaHelpTabId));
  const newToolPanelParent = layoutJson.getNodeById(parentId!)?.getParent();
  newToolPanelParent && layoutJson.doAction(Actions.updateNodeAttributes(newToolPanelParent.getId(), { width }));
};
export const addFormulaHelpTab = (layoutJson: Model) => {
  if (layoutJson && !layoutJson.getNodeById(formulaHelpTabId)) {
    const toolPanelParent = layoutJson.getNodeById(toolsPanelTabId)?.getParent();
    if (toolPanelParent) {
      const width =
        (toolPanelParent?.getRect()?.width || 0) !== 0 ? toolPanelParent?.getRect().width : SIDE_PANEL_INITIAL_WIDTH;

      layoutJson.doAction(Actions.addNode(formulaHelpTabJson, toolPanelParent.getId(), DockLocation.BOTTOM, 1));
      const tabSetParent = layoutJson.getNodeById(formulaHelpTabId)?.getParent();
      tabSetParent &&
        layoutJson.doAction(
          Actions.updateNodeAttributes(tabSetParent.getId(), { height: window.screen.availHeight / 4 }),
        );
      const newToolPanelParent = layoutJson.getNodeById(toolsPanelTabId)?.getParent();
      const row = newToolPanelParent?.getParent();
      row && layoutJson.doAction(Actions.updateNodeAttributes(row.getId(), { width }));
    }
  }

  layoutJson.doAction(Actions.selectTab(toolsPanelTabId));
  return layoutJson;
};

export const inSameTabset = (tab1: string, tab2: string, layoutModel: Model) => {
  const tab1Parent = layoutModel?.getNodeById(tab1)?.getParent();
  const tab2Parent = layoutModel?.getNodeById(tab2)?.getParent();
  return tab1Parent?.getId() === tab2Parent?.getId();
};

export const getParentTabSetCount = (tabId: string, layoutModel: Model) => {
  return layoutModel?.getNodeById(tabId)?.getParent()?.getChildren()?.length;
};

export const sideTabsArePristine = (layoutModel: Model) => {
  return (
    (inSameTabset(dataPanelTabId, toolsPanelTabId, layoutModel) &&
      inSameTabset(toolsPanelTabId, journalPanelTabId, layoutModel) &&
      getParentTabSetCount(dataPanelTabId, layoutModel) === 3) ||
    (inSameTabset(propertiesTabId, commentsTabId, layoutModel) &&
      getParentTabSetCount(propertiesTabId, layoutModel) === 2)
  );
};

export const initializeLayoutModel = (
  layoutJson: Model,
  worksheetsCollapsed: boolean,
  view: WorksheetView,
  isTopic: boolean,
  isVantage: boolean,
  addAssetGroupTab: boolean,
  addTableDefinitionViewTab: boolean,
  addStoredTableEditorTab: boolean,
  conditionInDetailsPanel: boolean,
  calculationHierarchyInTab: boolean,
  formulaInTab: boolean,
  selectedTab: string | undefined,
  isLocked: boolean,
): Model => {
  // users may forget that they had expanded a tabSet and then find themselves looking for lost tabs - to avoid
  // this we ensure all tabSets are minimized on load (or reset)
  const maximizedTabSet = layoutJson.getMaximizedTabset();
  if (maximizedTabSet) {
    layoutJson.doAction(Actions.maximizeToggle(maximizedTabSet.getId()));
  }
  if (worksheetsCollapsed) {
    const worksheetTab = layoutJson.getNodeById(worksheetsTabId);

    if (worksheetTab) {
      const worksheetTabParent = worksheetTab.getParent();
      worksheetTabParent &&
        layoutJson.doAction(
          Actions.updateNodeAttributes(worksheetTabParent.getId(), {
            selected: -1,
          }),
        );
    }
  }

  if (!isTopic && !isVantage) {
    // then it's a reset layout call - we have to check for details and capsule panels and add them if needed:
    const { capsules } = detailsAndCapsulePanelVisibilityByView(view?.key);
    if (isSystemTest()) {
      const trendTabSetParentId = layoutJson.getNodeById(trendTabSetId)?.getId();
      trendTabSetParentId &&
        layoutJson.doAction(
          Actions.updateNodeAttributes(trendTabSetParentId, {
            weight: 90,
          }),
        );
    }

    if (capsules && conditionInDetailsPanel && !layoutJson.getNodeById(capsulePanelTabId)) {
      addCapsulePanel(layoutJson);
    }
  }

  const trendParentTabSetId = layoutJson.getNodeById(trendTabId)?.getParent()?.getId();

  if (addAssetGroupTab) {
    if (!layoutJson.getNodeById(assetGroupTabId) && trendParentTabSetId) {
      layoutJson.doAction(Actions.addNode(assetGroupTabJson, trendParentTabSetId, DockLocation.CENTER, -1));
    }
  } else {
    layoutJson.doAction(Actions.deleteTab(assetGroupTabId));
  }
  if (
    addTableDefinitionViewTab &&
    (hasScalingTableCapability() || isScalingTableEditorEnabled()) &&
    hasValidFeatureInLicense(sqLicenseManagementStore.getState().license, Feature.Scaling_Tables)
  ) {
    if (!layoutJson.getNodeById(tableDefinitionTabId) && trendParentTabSetId) {
      layoutJson.doAction(Actions.addNode(tableDefinitionTabJson, trendParentTabSetId, DockLocation.CENTER, -1));
    }
  } else {
    layoutJson.doAction(Actions.deleteTab(tableDefinitionTabId));
  }

  if (addStoredTableEditorTab && isStoredTableItemFinderEnabled()) {
    if (!layoutJson.getNodeById(storedTableTabId) && trendParentTabSetId) {
      layoutJson.doAction(Actions.addNode(storedTableTabJson, trendParentTabSetId, DockLocation.CENTER, -1));
    }
  } else {
    layoutJson.doAction(Actions.deleteTab(storedTableTabId));
  }

  if (calculationHierarchyInTab) {
    if (!layoutJson.getNodeById(calculationHierarchyTabId) && trendParentTabSetId) {
      layoutJson.doAction(Actions.addNode(calculationHierarchyTabJson, trendParentTabSetId, DockLocation.CENTER, -1));
    }
  } else {
    layoutJson.doAction(Actions.deleteTab(calculationHierarchyTabId));
  }

  if (isLocked) {
    layoutJson.doAction(Actions.deleteTab(dataPanelTabId));
    layoutJson.doAction(Actions.deleteTab(formulaHelpTabId));
    removeFormulaHelpTab(layoutJson);
  }

  if (formulaInTab) {
    addFormulaHelpTab(layoutJson);
  } else {
    removeFormulaHelpTab(layoutJson);
  }

  // make sure the visibility listeners are always added
  layoutJson.getNodeById(trendTabId)?.setEventListener('visibility', ({ visible }) => {
    setTrendVisible(visible);
  });

  layoutJson.getNodeById(assetGroupTabId)?.setEventListener('visibility', ({ visible }) => {
    setAssetGroupEditorVisible(visible);
  });

  layoutJson.getNodeById(tableDefinitionTabId)?.setEventListener('visibility', ({ visible }) => {
    setTableDefinitionEditorVisible(visible);
  });

  layoutJson.getNodeById(journalTabId)?.setEventListener('visibility', ({ visible }) => {
    setJournalTabVisible(visible);
  });

  layoutJson.getNodeById(toolsPanelTabId)?.setEventListener('visibility', ({ visible }) => {
    setToolsTabVisible(visible);
  });

  if (getWorkbookDisplayMode() === WORKBOOK_DISPLAY.VIEW) {
    if (isTopic) {
      layoutJson.doAction(Actions.selectTab(propertiesTabId));
    } else if (isVantage) {
      layoutJson.doAction(Actions.selectTab(vantageTableTabId));
    } else {
      layoutJson.doAction(Actions.selectTab(journalPanelTabId));
    }
  } else if (!isMobileDevice() && selectedTab && sideTabsArePristine(layoutJson))
    layoutJson.doAction(Actions.selectTab(selectedTab));

  return layoutJson;
};

export const doResetLayout = () => {
  resetLayout(true);
  resetJournalToolbar(true);
  setFormulaInTab(false);
};

// calculate the panel weight based on the available width and the desired side panel width - it will return a weight
// that will result in the side panel having the desired pixel width when the layout is rendered
export function calculatePanelWeight(availableWidthPx: number, desiredSidePanelWidthPx: number) {
  const totalWeight = 100;
  return (desiredSidePanelWidthPx / availableWidthPx) * totalWeight;
}

// flex-layout-react does not support setting pixel widths for panels any longer, but we really want our side panels
// to start out at a given pixel width and not a proportional weight so we calculate the initial weight based on the
// current width of the container and the desired width of the side panel.
export function applySidePanelInitialWeight(
  layoutJson: IJsonModel,
  collapsibleSidePanelId: string,
  otherPanelId: string,
  containerWidthPx: number,
  desiredSidePanelWidthPx: number,
): IJsonModel {
  const root = layoutJson.layout;
  if (root.type !== 'row' || !Array.isArray(root.children)) {
    throw new Error('Expected layout root to be a row with children');
  }

  const totalWeight = 100;
  const sidePanelWeight = calculatePanelWeight(containerWidthPx, desiredSidePanelWidthPx);
  const otherPanelWeight = totalWeight - sidePanelWeight;

  const updatedChildren = root.children.map((child) => {
    if (child.id === collapsibleSidePanelId) {
      return {
        ...child,
        weight: sidePanelWeight,
      };
    }
    if (child.id === otherPanelId) {
      return {
        ...child,
        weight: otherPanelWeight,
      };
    }
    return child;
  });

  return {
    ...layoutJson,
    layout: {
      ...layoutJson.layout,
      children: updatedChildren,
    },
  };
}
